import {
    type ChipHandle,
    formatAgentTraceChipLabel,
    shortEntityId,
} from '@apps/web/features/admin_agent_dock/chip_registry';
import { type components } from '@libraries/typescript/api_client/generated_api_types';

// Per-kind chip context providers (PHO-14248 pattern): the ONE place
// that maps a kind's fast-summary read into the chip the dock
// attaches — label plus the structured facts block the agent kickoff
// carries. The detail pages use these for their route auto-chips and
// the "+ add to chat" affordances use the same builders, so a chip
// carries identical kickoff context no matter which surface attached
// it.

type AccountOrganizationContextRead =
    components['schemas']['AccountOrganizationContextRead'];
type AgentTraceSummaryRead = components['schemas']['AgentTraceSummaryRead'];
type AdminAgentNoteRead = components['schemas']['AdminAgentNoteRead'];

// The preview rides in the kickoff facts on every turn — keep it a
// sentence-scale head of the note body; the agent reads the full
// note through its tools when it needs more.
const NOTE_CONTENT_PREVIEW_MAX_CHARS = 240;

export function buildOrganizationChip(
    context: AccountOrganizationContextRead,
): ChipHandle {
    return {
        kind: 'organization',
        entity_id: context.organization_id,
        label: context.organization_name,
        facts: {
            kind: 'organization',
            organization_id: context.organization_id,
            organization_name: context.organization_name,
            // The Phoebe org's own lifecycle, not the Core CRM
            // account field — these are distinct contracts with
            // different value sets.
            lifecycle_stage: context.lifecycle_stage ?? null,
            integrations: context.integrations,
            open_agent_notes_count: context.open_agent_notes_count,
            on_call_rotation: context.on_call_rotation ?? null,
        },
    };
}

export function buildAgentTraceChip(
    conversationId: string,
    summary: AgentTraceSummaryRead,
    nowMs: number = Date.now(),
): ChipHandle {
    return {
        kind: 'agent_trace',
        entity_id: conversationId,
        label: formatAgentTraceChipLabel(
            conversationId,
            summary.created_at,
            nowMs,
        ),
        facts: {
            kind: 'agent_trace',
            trace_id: conversationId,
            run_id: summary.phoebe_run_id ?? null,
            root_span: summary.root_span ?? null,
            total_tool_calls: summary.tool_call_count,
            total_cost_usd:
                summary.total_cost_usd === null
                    ? null
                    : String(summary.total_cost_usd),
            cache_hit_rate: summary.cache_hit_rate ?? null,
            outcome: summary.run_status ?? null,
            top_level_model: summary.active_model ?? null,
        },
    };
}

export function buildAgentNoteChip(note: AdminAgentNoteRead): ChipHandle {
    const normalizedContent = note.content.replace(/\s+/g, ' ').trim();
    return {
        kind: 'agent_note',
        entity_id: note.id,
        label: `Note ${shortEntityId(note.id)} · ${note.organization_name}`,
        facts: {
            kind: 'agent_note',
            note_id: note.id,
            organization_id: note.organization_id,
            organization_name: note.organization_name,
            status: note.status,
            source: note.source,
            domains: [...note.classification_summary.domains],
            item_count: note.classification_summary.item_count,
            content_preview:
                normalizedContent.length > NOTE_CONTENT_PREVIEW_MAX_CHARS
                    ? `${normalizedContent.slice(0, NOTE_CONTENT_PREVIEW_MAX_CHARS - 1)}…`
                    : normalizedContent,
        },
    };
}

/**
 * Chip for a note's source SMS conversation. Callers gate on the
 * note actually having an SMS-conversation source; a note without
 * one has no conversation identity, so this throws rather than
 * fabricating a facts-less chip.
 */
export function buildConversationChipFromNote(
    note: AdminAgentNoteRead,
): ChipHandle {
    const conversationId =
        note.links.source_link_kind === 'sms_conversation'
            ? note.links.source_link_id
            : null;
    if (!conversationId) {
        throw new Error('Agent note has no SMS-conversation source');
    }
    return {
        kind: 'conversation',
        entity_id: conversationId,
        label: `SMS ${shortEntityId(conversationId)} · ${note.organization_name}`,
        facts: {
            kind: 'conversation',
            conversation_id: conversationId,
            organization_id: note.organization_id,
            organization_name: note.organization_name,
            channel: 'sms',
            source_note_id: note.id,
            source_rating: note.links.source_rating ?? null,
        },
    };
}
